import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import { RbacPortalScopeService } from './rbac-portal-scope.service';

export interface RoleRow {
  id: number;
  role_name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  deleted_by?: number | null;
}

export interface RoleListMeta {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  prev: number | null;
  next: number | null;
}

export interface RoleListPayload {
  result: RoleRow[];
  meta: RoleListMeta;
}

export interface CreateRolePayload {
  role_name: string;
  slug: string;
  description?: string;
}

export interface UpdateRolePayload {
  role_name: string;
  slug: string;
  description?: string;
  is_active?: boolean;
}

export interface PermissionRecord {
  id: number;
  permissions_name: string;
  slug: string;
  description: string | null;
  model: string | null;
  is_active: boolean;
}

export interface RolePermissionsPayload {
  role_id: number;
  permission_ids: number[];
  permissions: PermissionRecord[];
}

export type PermissionsGrouped = Record<string, PermissionRecord[]>;

export interface RoleListQuery {
  page?: number;
  page_size?: number;
  searchText?: string;
  sortColumn?: 'description' | 'slug' | 'role_name' | 'created_at' | 'deleted_at';
  sortBy?: 'ASC' | 'DESC';
  status?: 'active' | 'deleted';
  is_active?: boolean;
}

@Injectable({ providedIn: 'root' })
export class RoleApiService {
  private readonly http = inject(HttpClient);
  private readonly rbacScope = inject(RbacPortalScopeService);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  private rolePath(suffix = ''): string {
    const prefix = this.rbacScope.roleApiPrefix();
    return `${this.base}${prefix}${suffix}`;
  }

  fetchRoleDropdown(): Observable<ApiEnvelope<RoleRow[]>> {
    return this.http.get<ApiEnvelope<RoleRow[]>>(this.rolePath('/dropdown'));
  }

  listRoles(query: RoleListQuery = {}): Observable<ApiEnvelope<RoleListPayload>> {
    let params = new HttpParams();
    if (query.page != null) params = params.set('page', query.page);
    if (query.page_size != null) params = params.set('page_size', query.page_size);
    if (query.searchText) params = params.set('searchText', query.searchText);
    if (query.sortColumn) params = params.set('sortColumn', query.sortColumn);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.status) params = params.set('status', query.status);
    if (query.is_active !== undefined) params = params.set('is_active', query.is_active);
    return this.http.get<ApiEnvelope<RoleListPayload>>(this.rolePath(), { params });
  }

  getRoleById(id: number): Observable<ApiEnvelope<RoleRow>> {
    return this.http.get<ApiEnvelope<RoleRow>>(this.rolePath(`/${id}`));
  }

  createRole(payload: CreateRolePayload): Observable<ApiEnvelope<RoleRow>> {
    return this.http.post<ApiEnvelope<RoleRow>>(this.rolePath(), payload);
  }

  updateRole(id: number, payload: UpdateRolePayload): Observable<ApiEnvelope<RoleRow>> {
    return this.http.patch<ApiEnvelope<RoleRow>>(this.rolePath(`/${id}`), payload);
  }

  toggleRoleStatus(id: number): Observable<ApiEnvelope<RoleRow>> {
    return this.http.patch<ApiEnvelope<RoleRow>>(this.rolePath(`/${id}/toggle-status`), {});
  }

  deleteRole(id: number): Observable<ApiEnvelope<null>> {
    return this.http.delete<ApiEnvelope<null>>(this.rolePath(`/${id}`));
  }

  restoreRole(id: number): Observable<ApiEnvelope<RoleRow>> {
    return this.http.patch<ApiEnvelope<RoleRow>>(this.rolePath(`/${id}/restore`), {});
  }

  permanentDeleteRole(id: number): Observable<ApiEnvelope<null>> {
    return this.http.delete<ApiEnvelope<null>>(this.rolePath(`/permanent-delete/${id}`));
  }

  fetchAllPermissions(): Observable<ApiEnvelope<PermissionsGrouped>> {
    const permBase = `${this.base}${this.rbacScope.permissionApiPrefix()}`;
    return this.http
      .get<ApiEnvelope<{ result?: PermissionsGrouped } & PermissionsGrouped>>(
        permBase,
      )
      .pipe(
        map((res) => {
          const raw = res?.data;
          const grouped =
            raw &&
            typeof raw === 'object' &&
            'result' in raw &&
            raw.result &&
            typeof raw.result === 'object'
              ? raw.result
              : (raw as PermissionsGrouped);
          return { ...res, data: grouped ?? {} };
        }),
      );
  }

  fetchRolePermissions(roleId: number): Observable<ApiEnvelope<RolePermissionsPayload>> {
    return this.http.get<ApiEnvelope<RolePermissionsPayload>>(
      this.rolePath(`/${roleId}/permissions`),
    );
  }

  updateRolePermissions(
    roleId: number,
    permissionIds: number[],
  ): Observable<ApiEnvelope<RolePermissionsPayload>> {
    return this.http.patch<ApiEnvelope<RolePermissionsPayload>>(
      this.rolePath(`/${roleId}/permissions`),
      { permission_ids: JSON.stringify(permissionIds) },
    );
  }
}
